// Pure helpers for the Workload Rebalance dialog (no React/UI here so they stay testable).

// Auto-distribute: move tasks off the busy person to the least-loaded teammate while it
// actually reduces imbalance (stops before overloading anyone else). Admins are excluded
// from the auto pool (delivery teammates only); they can still be picked manually.
// Returns { targets: {task_id -> user_id}, reason: "no-pool" | "balanced" | "ok" }.
export function computeEvenSplit(person, members, tasks, perPerson = {}) {
  const pool = members.filter((m) => m.role !== "admin");
  if (pool.length === 0) return { targets: {}, reason: "no-pool" };

  const counts = { ...perPerson };
  pool.forEach((m) => { if (counts[m.user_id] === undefined) counts[m.user_id] = 0; });
  if (counts[person.user_id] === undefined) counts[person.user_id] = tasks.length;

  const targets = {};
  tasks.forEach((t) => {
    let best = null;
    let bestC = Infinity;
    pool.forEach((m) => { if (counts[m.user_id] < bestC) { bestC = counts[m.user_id]; best = m.user_id; } });
    if (best !== null && counts[best] < counts[person.user_id] - 1) {
      targets[t.task_id] = best;
      counts[best] += 1;
      counts[person.user_id] -= 1;
    }
  });
  return { targets, reason: Object.keys(targets).length === 0 ? "balanced" : "ok" };
}

// Before -> after open-task counts for the busy person + each chosen teammate.
// Returns { after: {uid -> count}, movedTargets: [uid] } preserving first-seen order.
export function computeAfterCounts(person, tasks, targets, perPerson = {}) {
  const after = {};
  const ensure = (uid) => { if (after[uid] === undefined) after[uid] = perPerson[uid] || 0; };
  ensure(person.user_id);

  const movedTargets = [];
  tasks.forEach((t) => {
    const tgt = targets[t.task_id];
    if (!tgt) return;
    ensure(tgt);
    after[person.user_id] -= 1;
    if (!(t.assigned_to || []).includes(tgt)) after[tgt] += 1;
    if (!movedTargets.includes(tgt)) movedTargets.push(tgt);
  });
  return { after, movedTargets };
}

// New assignee list for a task once the busy person is swapped for the chosen target.
export function nextAssignees(task, personId, target) {
  return Array.from(new Set([...(task.assigned_to || []).filter((u) => u !== personId), target]));
}
